import { CALL_PRICING_MODEL_FLAT_RATE } from '../../../../../shared/constants/common';
import { JUICYHOUSE_CALL_EVENT } from '../../../../../shared/utils/matrix/matrixRoomUtils';

const VALID_PRICING_MODELS = new Set(['flat_rate', 'per_minute']);

const normalizeMediaType = (mediaType) => (mediaType === 'video' ? 'video' : 'voice');

export const parsePositiveNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const parseNonNegativeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

export const clampFlatRateDurationMinutes = (raw) => {
  const next = Number(raw);
  if (!Number.isFinite(next)) return CALL_PRICING_MODEL_FLAT_RATE.MIN_DURATION_MINUTES;
  return Math.min(
    CALL_PRICING_MODEL_FLAT_RATE.MAX_DURATION_MINUTES,
    Math.max(CALL_PRICING_MODEL_FLAT_RATE.MIN_DURATION_MINUTES, Math.round(next))
  );
};

export const parseCallRateOverrideFromQuery = ({
  query,
  queryPricingModelRaw,
  shouldAutoPlaceCall,
}) => {
  if (!shouldAutoPlaceCall) return null;
  const pricingModel =
    queryPricingModelRaw === 'flat_rate' || queryPricingModelRaw === 'per_minute'
      ? queryPricingModelRaw
      : null;
  if (!pricingModel) return null;

  const perMinuteAmount = parsePositiveNumber(query.get('perMinuteAmount'));
  const minDurationMinutes = parseNonNegativeNumber(query.get('minDurationMinutes'));
  const flatRateAmount = parsePositiveNumber(query.get('flatRateAmount'));
  const flatRateDurationMinutes = parsePositiveNumber(query.get('flatRateDurationMinutes'));

  if (pricingModel === 'per_minute' && !perMinuteAmount) return null;
  if (pricingModel === 'flat_rate' && (!flatRateAmount || !flatRateDurationMinutes)) return null;

  return {
    pricingModel,
    perMinuteAmount,
    minDurationMinutes:
      minDurationMinutes == null ? 0 : Math.max(0, Math.round(minDurationMinutes)),
    flatRateAmount,
    flatRateDurationMinutes: flatRateDurationMinutes
      ? clampFlatRateDurationMinutes(flatRateDurationMinutes)
      : null,
  };
};

/**
 * Build voice/video call settings for updateCreator (same shape as edit profile).
 */
export const buildCallSettingsProfileUpdatePayload = (
  existingSettings = {},
  {
    pricingModel,
    perMinuteAmount,
    minCallMinutess,
    flatRateAmount,
    flatRateDurationMinutes,
  }
) => {
  const normalizedMinCallMinutess = Math.max(0, Number(minCallMinutess) || 0);
  const normalizedDurationMinutes = clampFlatRateDurationMinutes(flatRateDurationMinutes);

  return {
    ...existingSettings,
    pricingModel,
    perMinute: {
      ...existingSettings?.perMinute,
      amount: perMinuteAmount,
      minCallMinutess: normalizedMinCallMinutess,
      minCallSeconds: normalizedMinCallMinutess * 60,
    },
    flatRate: {
      ...existingSettings?.flatRate,
      amount: flatRateAmount,
      durationMinutes: normalizedDurationMinutes,
      durationSeconds: normalizedDurationMinutes * 60,
    },
  };
};

export const buildCreatorOutgoingCallRateOffer = (callRateOverride) => {
  if (!callRateOverride) return null;
  return {
    pricingModel: callRateOverride.pricingModel,
    perMinute:
      callRateOverride.pricingModel === 'per_minute'
        ? {
            amount: Number(callRateOverride.perMinuteAmount) || 0,
            minCallMinutess: Math.max(0, Number(callRateOverride.minDurationMinutes) || 0),
          }
        : null,
    flatRate:
      callRateOverride.pricingModel === 'flat_rate'
        ? {
            amount: Number(callRateOverride.flatRateAmount) || 0,
            durationMinutes: Number(callRateOverride.flatRateDurationMinutes) || 0,
          }
        : null,
  };
};

export const buildCallRateOfferEventContent = ({
  callRateOverride,
  callMediaType,
  senderUserId,
}) => {
  if (!callRateOverride) return null;
  return {
    type: 'call_rate_offer',
    callMediaType,
    ...buildCreatorOutgoingCallRateOffer(callRateOverride),
    sentAtMs: Date.now(),
    senderUserId: senderUserId || null,
  };
};

/**
 * Find the latest in-room call rate offer for the given media type.
 */
export const findLatestCallRateOffer = ({
  matrixRoom,
  mediaType,
  maxAgeMs = 5 * 60 * 1000,
  expectedSenderUserId = null,
}) => {
  if (!matrixRoom) return null;
  try {
    const timelineEvents = matrixRoom.getLiveTimeline?.()?.getEvents?.() || [];
    if (!Array.isArray(timelineEvents) || timelineEvents.length === 0) return null;

    const normalizedMediaType = normalizeMediaType(mediaType);
    const now = Date.now();

    for (let i = timelineEvents.length - 1; i >= 0; i -= 1) {
      const event = timelineEvents[i];
      if (event?.getType?.() !== JUICYHOUSE_CALL_EVENT) continue;
      const content = event.getContent?.() || {};
      if (content?.type !== 'call_rate_offer') continue;
      if (content?.callMediaType !== normalizedMediaType) continue;

      const sentAtMs = Number(content?.sentAtMs) || 0;
      if (sentAtMs > 0 && now - sentAtMs > maxAgeMs) continue;

      const eventSender = event.getSender?.() || content?.senderUserId || null;
      if (expectedSenderUserId && eventSender && eventSender !== expectedSenderUserId) continue;

      const pricingModel = VALID_PRICING_MODELS.has(content?.pricingModel)
        ? content.pricingModel
        : null;
      if (!pricingModel) continue;

      return {
        pricingModel,
        perMinute: content?.perMinute || null,
        flatRate: content?.flatRate || null,
      };
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Merge transient in-call pricing offer into creator pricing for one media type.
 */
export const mergeCallRateIntoCreatorInfo = ({
  creatorInfo,
  mediaType,
  callRateOffer,
}) => {
  if (!creatorInfo || !callRateOffer) return creatorInfo;
  const mediaKey = normalizeMediaType(mediaType);

  return {
    ...creatorInfo,
    [mediaKey]: {
      ...(creatorInfo?.[mediaKey] || {}),
      pricingModel: callRateOffer.pricingModel,
      perMinute: callRateOffer.perMinute
        ? {
          ...(creatorInfo?.[mediaKey]?.perMinute || {}),
          ...callRateOffer.perMinute,
        }
        : creatorInfo?.[mediaKey]?.perMinute,
      flatRate: callRateOffer.flatRate
        ? {
          ...(creatorInfo?.[mediaKey]?.flatRate || {}),
          ...callRateOffer.flatRate,
        }
        : creatorInfo?.[mediaKey]?.flatRate,
    },
  };
};
